// Fat Loss Report Configuration
// Contains biomarker data, ranges, interpretations, and recommendations

export const fatLossBiomarkers = {
  bodyFatPercentage: {
    label: 'Body Fat Percentage',
    unit: '%',
    icon: 'favorite',
    rangeFormat: 'full', // Display full range (min-max)
    ranges: {
      optimal: { min: 11, max: 26, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 11, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 26, max: 100, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'This shows what portion of your total body weight is made up of fat. A higher body fat percentage means a larger share of your weight is stored energy rather than muscle or bone.',
    whyItMatters: 'Tracking body fat percentage helps determine whether weight changes are coming from fat loss rather than muscle loss.'
  },

  almi: {
    label: 'Muscle Mass (ALMI)',
    unit: 'kg/m',
    icon: 'accessibility',
    rangeFormat: 'min', // Display only minimum with >=
    ranges: {
      optimal: { min: 7, max: 100, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 7, status: 'AT RISK', color: '#E7AFAF' }
    },
    genderRanges: {
      Male: {
        optimal: { min: 7.0, max: 100, status: 'OPTIMAL', color: '#C4E7AF' },
        atrisk: { min: 0, max: 7.0, status: 'AT RISK', color: '#E7AFAF' }
      },
      Female: {
        optimal: { min: 5.5, max: 100, status: 'OPTIMAL', color: '#C4E7AF' },
        atrisk: { min: 0, max: 5.5, status: 'AT RISK', color: '#E7AFAF' }
      }
    },
    description: 'Appendicular Lean Mass Index (ALMI) reflects the amount of muscle in your arms and legs relative to your height. A low ALMI indicates lower overall muscle mass.',
    whyItMatters: 'Muscle helps burn calories even at rest. Lower muscle mass reduces metabolic rate, making fat loss slower and easier to regain.'
  },

  visceralFatArea: {
    label: 'Visceral Fat Area',
    unit: 'cm²',
    icon: 'favorite_border',
    rangeFormat: 'max', // Display only maximum with <
    ranges: {
      optimal: { min: 0, max: 100, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 100, max: 10000, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'This measures fat stored deep inside the abdomen, around vital organs like the liver and intestines. This type of fat is more harmful than fat under the skin.',
    whyItMatters: 'High visceral fat interferes with metabolism, increases insulin resistance, and makes fat loss harder. Reducing visceral fat significantly improves metabolic health.'
  },

  astAltRatio: {
    label: 'AST/ALT Ratio',
    unit: '',
    icon: 'favorite',
    rangeFormat: 'full', // Display full range (min-max)
    ranges: {
      optimal: { min: 0.8, max: 1.2, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 0.8, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 1.2, max: 100, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'This ratio compares two liver enzymes. A higher ratio can be an early marker of fat accumulation in the liver (fatty liver).',
    whyItMatters: 'Fatty liver impairs how the body processes fats and sugars, making fat loss more difficult until liver health improves.'
  },

  triglycerides: {
    label: 'Triglycerides',
    unit: 'mg/dl',
    icon: 'favorite',
    rangeFormat: 'max', // Display only maximum with <
    ranges: {
      optimal: { min: 0, max: 150, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 150, max: 10000, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'Triglycerides are a form of fat circulating in the blood, often elevated when carbohydrate intake exceeds what the body can efficiently handle.',
    whyItMatters: 'High triglyceride levels signal poor fat and carbohydrate metabolism, which can slow fat loss and increase storage.'
  },

  fastingInsulin: {
    label: 'Fasting Insulin',
    unit: 'mU/L',
    icon: 'favorite',
    ranges: {
      optimal: { min: 0, max: 12, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 12, max: 10000, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'When insulin is elevated, it prevents fat cells from releasing fat. Fat loss is biologically blocked and your body is in fat storage mode.',
    whyItMatters: 'High fasting insulin is the #1 blocker of fat loss. It signals your body to store energy rather than burn it.'
  },

  homaIr: {
    label: 'HOMA-IR',
    unit: '',
    icon: 'favorite',
    ranges: {
      optimal: { min: 0, max: 2, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 2, max: 100, status: 'AT RISK', color: '#E7AFAF' }
    },
    description: 'HOMA-IR (Homeostatic Model Assessment for Insulin Resistance) measures how resistant your cells are to insulin.',
    whyItMatters: 'More insulin needed to control blood sugar means more fat storage signals. Higher HOMA-IR makes fat loss biologically harder.'
  },

  cortisol: {
    label: 'Cortisol',
    unit: 'µg/dL',
    icon: 'favorite',
    ranges: {
      optimal: { min: 5, max: 25, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 5, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 25, max: 1000, status: 'ELEVATED', color: '#E7AFAF'}
    },
    description: 'Cortisol is your stress hormone. Chronically elevated cortisol promotes belly fat storage and prevents fat loss.',
    whyItMatters: 'High cortisol signals your body to store fat, especially around the abdomen, and can block fat release even with calorie deficit.'
  },

  tsh: {
    label: 'TSH',
    unit: 'mIU/L',
    icon: 'favorite',
    ranges: {
      optimal: { min: 0.4, max: 4, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 0.4, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 4, max: 1000, status: 'ELEVATED', color: '#E7AFAF'}
    },
    description: 'TSH (Thyroid Stimulating Hormone) controls your metabolic rate. Low or high TSH indicates thyroid dysfunction.',
    whyItMatters: 'Thyroid dysfunction directly impacts metabolism. Abnormal TSH makes fat loss significantly harder.'
  },

  freeT3: {
    label: 'Free T3',
    unit: 'pg/mL',
    icon: 'favorite',
    ranges: {
      optimal: { min: 2.3, max: 4.2, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 0, max: 2.3, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 4.2, max: 10000, status: 'ELEVATED', color: '#E7AFAF'}
    },
    description: 'Free T3 is the active thyroid hormone that directly controls metabolic rate.',
    whyItMatters: 'Low Free T3 means slower metabolism and reduced fat-burning capacity, even with proper diet and exercise.'
  },

  hsCrp: {
    label: 'hsCRP',
    unit: 'mg/L',
    icon: 'favorite',
    ranges: {
      optimal: { min: 0, max: 1, status: 'OPTIMAL', color: '#C4E7AF' },
      atrisk: { min: 1, max: 3, status: 'AT RISK', color: '#E7AFAF' },
      elevated: { min: 3, max: 1000, status: 'ELEVATED', color: '#E7AFAF'}
    },
    description: 'hsCRP (high-sensitivity C-Reactive Protein) measures chronic low-grade inflammation in your body.',
    whyItMatters: 'Chronic inflammation blocks fat loss, increases insulin resistance, and promotes fat storage, especially visceral fat.'
  },

  restingMetabolicRate: {
    label: 'Resting Metabolic Rate',
    unit: 'kcals/day',
    icon: 'favorite',
    description: 'Your Resting Metabolic Rate represents the number of calories your body needs each day to sustain vital functions at rest like breathing, circulation, and cellular repair. It forms the foundation of your energy balance.',
    whyItMatters: 'This is your baseline. Consuming significantly below this triggers metabolic adaptation (lower RMR), making fat loss more difficult.'
  },

  targetCalories: {
    label: 'Target Calories',
    unit: 'kcals/day',
    icon: 'favorite',
    description: 'This target supports sustainable fat loss while preserving metabolic function. Consuming below this threshold triggers metabolic adaptation (lower RMR), making fat loss more difficult until the body can recover.',
    whyItMatters: 'Aggressive calorie restriction backfires by lowering metabolism. This target balances fat loss with metabolic preservation.'
  },

  zone2HeartRate: {
    label: 'Zone 2 Heart Rate',
    unit: 'bpm',
    icon: 'favorite',
    description: 'Zone 2 training refers to moderate-intensity exercise performed at a heart rate where fat is the primary fuel source. At this intensity, the body improves its ability to burn fat, increase mitochondrial efficiency, and sustain metabolic capacity required for efficient fat loss.',
    whyItMatters: 'Zone 2 training is the most efficient way to improve fat-burning capacity without triggering excessive stress or muscle loss.'
  }
};

export const getStatusForValue = (biomarker, value, gender = null) => {
  const biomarkerConfig = fatLossBiomarkers[biomarker];
  if (!biomarkerConfig) return null;

  // Use gender-specific ranges if available and gender is provided
  let ranges = biomarkerConfig.ranges;
  if (gender && biomarkerConfig.genderRanges && biomarkerConfig.genderRanges[gender]) {
    ranges = biomarkerConfig.genderRanges[gender];
  }

  if (!ranges) return null;

  for (const [key, range] of Object.entries(ranges)) {
    if (value >= range.min && value <= range.max) {
      return {
        status: range.status,
        color: range.color,
        key: key
      };
    }
  }
  return null;
};

// Helper function to get range string for a biomarker (with optional gender support)
export const getRangeStringForBiomarker = (biomarkerKey, gender = null) => {
  const biomarkerConfig = fatLossBiomarkers[biomarkerKey];
  if (!biomarkerConfig) return '';

  // Use gender-specific ranges if available
  let ranges = biomarkerConfig.ranges;
  if (gender && biomarkerConfig.genderRanges && biomarkerConfig.genderRanges[gender]) {
    ranges = biomarkerConfig.genderRanges[gender];
  }

  if (!ranges) return '';

  const optimalRange = ranges.optimal;
  if (!optimalRange) return '';

  // Determine range format (default to 'full' if not specified)
  const rangeFormat = biomarkerConfig.rangeFormat || 'full';

  switch (rangeFormat) {
    case 'min':
      // Display only minimum with >= (e.g., ">=7")
      return `(>=${optimalRange.min})`;
    case 'max':
      // Display only maximum with < (e.g., "<100")
      return `(<${optimalRange.max})`;
    case 'full':
    default:
      // Display full range (e.g., "0.8-1.2")
      return `(${optimalRange.min}-${optimalRange.max})`;
  }
};

export const fatLossRecommendations = {
  highInsulin: [
    'Reduce meal frequency to 2-3 meals per day',
    'Prioritize protein-first at every meal',
    'Eliminate processed carbohydrates',
    'Include strength training 3x per week',
    'Improve sleep duration to 7-9 hours'
  ],
  highVisceral: [
    'Increase Zone 2 cardio to 150+ minutes per week',
    'Reduce refined carbohydrates',
    'Increase fiber intake',
    'Prioritize sleep quality',
    'Consider intermittent fasting protocols'
  ],
  lowMuscle: [
    'Prioritize strength training 3-4x per week',
    'Increase protein intake to 1.6-2.2g per kg body weight',
    'Focus on progressive overload',
    'Ensure adequate calorie intake',
    'Include adequate recovery days'
  ],
  highInflammation: [
    'Increase omega-3 fatty acid intake',
    'Reduce processed foods and seed oils',
    'Increase antioxidant-rich foods',
    'Prioritize sleep and stress management',
    'Consider anti-inflammatory supplements'
  ],
  thyroidDysfunction: [
    'Consult with an endocrinologist',
    'Consider thyroid medication if needed',
    'Ensure adequate iodine and selenium intake',
    'Reduce stress and improve sleep',
    'Avoid excessive cardio which can suppress thyroid'
  ]
};

export const fatLossBlockersSummary = (reportData, gender = null) => {
  const blockers = [];
  
  if (reportData.fastingInsulin > 12) {
    blockers.push('High fasting insulin is preventing fat release');
  }
  if (reportData.visceralFatArea > 100) {
    blockers.push('Elevated visceral fat indicates metabolic dysfunction');
  }
  
  // Gender-specific ALMI threshold
  let almiThreshold = 7; // Default for male
  if (gender && gender.toLowerCase() === 'female') {
    almiThreshold = 5.5;
  }
  if (reportData.almi < almiThreshold) {
    blockers.push('Low muscle mass is reducing your metabolic rate');
  }
  
  if (reportData.hsCrp > 1) {
    blockers.push('Chronic inflammation is blocking fat loss');
  }
  if (reportData.tsh < 0.4 || reportData.tsh > 4) {
    blockers.push('Thyroid dysfunction is affecting metabolism');
  }

  return blockers.length > 0 
    ? blockers.join('. ') + '.'
    : 'Your metabolic markers are well-balanced for fat loss.';
};
